import { TFunction } from "i18next";
import { z } from "zod";

import { FacilityType } from "../types/facility.definition";
import { AcceptedCountries, CivilityEnum } from "../types/user.definition";
import { isPhoneNumberValidForCountry } from "../utils/phone-utils";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * MED-1543: this rule used to be /^\+?[\d\s\-().]{6,20}$/, which accepts
 * anything from six to twenty characters — including the reported
 * "+22178000012299", two digits too long for Senegal. The country's own digit
 * count is the rule, and it lives in one place now.
 */
const phoneNumber = (t: TFunction) =>
  z
    .string()
    .min(1, t("validation.required"))
    .refine(isPhoneNumberValidForCountry, t("validation.phoneInvalid"));

// ── Edit Facility ─────────────────────────────────────────────────────────

export const editFacilitySchema = (t: TFunction) =>
  z.object({
    name: z.string().min(1, t("validation.required")),
    contactEmail: z
      .string()
      .min(1, t("validation.required"))
      .regex(EMAIL_REGEX, t("validation.emailInvalid")),
    phoneNumber: phoneNumber(t),
    website: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val) return true;
          const url =
            val.startsWith("http://") || val.startsWith("https://")
              ? val
              : `https://${val}`;
          try {
            new URL(url);
            return true;
          } catch {
            return false;
          }
        },
        { message: t("validation.urlInvalid") }
      ),
    facilityType: z.enum(
      Object.values(FacilityType) as [FacilityType, ...FacilityType[]],
      { error: () => ({ message: t("validation.required") }) }
    ),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    accessInformation: z.string().optional(),
    country: z.enum(
      Object.values(AcceptedCountries) as [
        AcceptedCountries,
        ...AcceptedCountries[],
      ]
    ),
    photoUrl: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val) return true;
          try {
            new URL(val);
            return true;
          } catch {
            return false;
          }
        },
        { message: t("validation.urlInvalid") }
      ),
  });

export type EditFacilityFormData = z.infer<
  ReturnType<typeof editFacilitySchema>
>;

// ── Edit Manager ──────────────────────────────────────────────────────────

export const editManagerSchema = (t: TFunction) =>
  z.object({
    civility: z.enum(Object.values(CivilityEnum) as [string, ...string[]], {
      error: () => ({ message: t("validation.required") }),
    }),
    firstName: z.string().min(1, t("validation.required")),
    lastName: z.string().min(1, t("validation.required")),
    phoneNumber: phoneNumber(t),
    // Optional: a manager is an administrative account and nobody collects a
    // birth date for one, but it is shown on the detail page and the manager
    // can set it themselves from the dashboard (MED-1423).
    dateOfBirth: z.string().optional(),
  });

export type EditManagerFormData = z.infer<ReturnType<typeof editManagerSchema>>;

// ── Create Manager (used in AssignManagerModal) ───────────────────────────

export const createManagerSchema = (t: TFunction) =>
  z.object({
    civility: z.enum(Object.values(CivilityEnum) as [string, ...string[]], {
      error: () => ({ message: t("validation.required") }),
    }),
    firstName: z.string().min(1, t("validation.required")),
    lastName: z.string().min(1, t("validation.required")),
    email: z
      .string()
      .min(1, t("validation.required"))
      .regex(EMAIL_REGEX, t("validation.emailInvalid")),
    phoneNumber: phoneNumber(t),
    // Optional: a manager is an administrative account and nobody collects a
    // birth date for one, but it is shown on the detail page and the manager
    // can set it themselves from the dashboard (MED-1423).
    dateOfBirth: z.string().optional(),
  });

export type CreateManagerFormData = z.infer<
  ReturnType<typeof createManagerSchema>
>;
